import {
  type FrameworkEntityAliases,
  type ParsedProjectPath,
  PROJECT_LEVEL_ROUTE_PATHS,
  type ProjectRouteKey,
  parseAssistantThreadIdFromPath,
  parseProjectPath,
} from "@repo/shared";
import { createContext, type ReactNode, useCallback, useContext, useMemo } from "react";
import type { ProjectFrameworkStructure } from "@/features/project/hooks/useProjectData";
import { buildFrameworkEntityAliases } from "@/features/project/lib/framework-registry";
import type { TabSearchParams } from "@/features/project/types";

type ProjectNavigate = (
  path: string,
  searchParams?: TabSearchParams,
  options?: { skipGuard?: boolean }
) => void;

interface ProjectRouteContextValue {
  parsed: ParsedProjectPath;
  currentPath: string;
  isFrameworkPending: boolean;
  isFrameworkMissing: boolean;
  frameworkStructure: ProjectFrameworkStructure | null | undefined;
  searchParams: TabSearchParams;
  navigate: ProjectNavigate;
}

interface ProjectRouteProviderProps {
  children?: ReactNode;
  currentPath: string;
  frameworkStructure: ProjectFrameworkStructure | null | undefined;
  searchParams?: TabSearchParams;
  navigate: ProjectNavigate;
}

const ProjectRouteContext = createContext<ProjectRouteContextValue | null>(null);

function getEntityAliases(
  structure: Parameters<typeof buildFrameworkEntityAliases>[0]
): FrameworkEntityAliases | null {
  return buildFrameworkEntityAliases(structure);
}

function parseAssistantRouteFallback(path: string): {
  routeKey: ProjectRouteKey;
  params: Record<string, string>;
} | null {
  if (path === PROJECT_LEVEL_ROUTE_PATHS.aiAssistantNewChat) {
    return { routeKey: "aiAssistantNewChat", params: {} };
  }
  if (path === PROJECT_LEVEL_ROUTE_PATHS.aiAssistantHistory) {
    return { routeKey: "aiAssistantHistory", params: {} };
  }
  const threadId = parseAssistantThreadIdFromPath(path);
  if (threadId) {
    return {
      routeKey: "aiAssistantThread",
      params: { threadId },
    };
  }
  return null;
}

function hasKnownRootFolder(structure: ProjectFrameworkStructure, rootFolderSlug?: string) {
  if (!rootFolderSlug) return true;

  return (structure.rootFolders ?? []).some((rootFolder) => {
    const candidateSlug = rootFolder.slug || rootFolder.key;
    return candidateSlug === rootFolderSlug || rootFolder.key === rootFolderSlug;
  });
}

export function ProjectRouteProvider({
  children,
  currentPath,
  frameworkStructure,
  searchParams = {},
  navigate: navigateProjectPath,
}: ProjectRouteProviderProps) {
  const assistantFallback = useMemo(() => parseAssistantRouteFallback(currentPath), [currentPath]);
  const isFrameworkPending = !assistantFallback && frameworkStructure === undefined;
  const isFrameworkMissing = !assistantFallback && frameworkStructure === null;
  const aliases = useMemo(() => getEntityAliases(frameworkStructure ?? null), [frameworkStructure]);

  const parsedPath = useMemo<ParsedProjectPath>(() => {
    if (assistantFallback) {
      return {
        routeKey: assistantFallback.routeKey,
        params: assistantFallback.params,
        normalizedPath: currentPath,
        canonicalPath: currentPath,
      };
    }
    if (isFrameworkPending || isFrameworkMissing) {
      return {
        routeKey: "notFound",
        params: { path: currentPath },
        normalizedPath: currentPath,
        canonicalPath: currentPath,
      };
    }
    return parseProjectPath(currentPath, {
      aliases,
      structure:
        frameworkStructure?.rootFolders && frameworkStructure?.folderNodes
          ? {
              rootFolders: frameworkStructure.rootFolders,
              folderNodes: frameworkStructure.folderNodes,
            }
          : null,
    });
  }, [
    assistantFallback,
    currentPath,
    aliases,
    frameworkStructure,
    isFrameworkMissing,
    isFrameworkPending,
  ]);

  const resolvedPath = useMemo<ParsedProjectPath>(() => {
    const rootFolderSlug =
      typeof parsedPath.params.rootFolderSlug === "string" ? parsedPath.params.rootFolderSlug : "";

    if (frameworkStructure && !hasKnownRootFolder(frameworkStructure, rootFolderSlug)) {
      return {
        routeKey: "notFound",
        params: { path: parsedPath.normalizedPath },
        normalizedPath: parsedPath.normalizedPath,
        canonicalPath: parsedPath.normalizedPath,
      };
    }

    return parsedPath;
  }, [frameworkStructure, parsedPath]);

  const navigate = useCallback<ProjectNavigate>(
    (path, searchParams, options) => navigateProjectPath(path, searchParams, options),
    [navigateProjectPath]
  );

  const value = useMemo<ProjectRouteContextValue>(
    () => ({
      parsed: resolvedPath,
      currentPath,
      isFrameworkPending,
      isFrameworkMissing,
      frameworkStructure,
      searchParams,
      navigate,
    }),
    [
      currentPath,
      frameworkStructure,
      isFrameworkMissing,
      isFrameworkPending,
      navigate,
      resolvedPath,
      searchParams,
    ]
  );

  return <ProjectRouteContext.Provider value={value}>{children}</ProjectRouteContext.Provider>;
}

export function useProjectRoute(): {
  parsed: ParsedProjectPath;
  currentPath: string;
  isFrameworkPending: boolean;
  isFrameworkMissing: boolean;
  frameworkStructure: ProjectFrameworkStructure | null | undefined;
} {
  const value = useContext(ProjectRouteContext);
  if (!value) {
    throw new Error("useProjectRoute must be used within ProjectRouteProvider");
  }
  return {
    parsed: value.parsed,
    currentPath: value.currentPath,
    isFrameworkPending: value.isFrameworkPending,
    isFrameworkMissing: value.isFrameworkMissing,
    frameworkStructure: value.frameworkStructure,
  };
}

export function useProjectNavigate(): ProjectNavigate {
  const value = useContext(ProjectRouteContext);
  if (!value) {
    throw new Error("useProjectNavigate must be used within ProjectRouteProvider");
  }
  return value.navigate;
}

export function useProjectSearchParams(): TabSearchParams {
  const value = useContext(ProjectRouteContext);
  if (!value) {
    throw new Error("useProjectSearchParams must be used within ProjectRouteProvider");
  }
  return value.searchParams;
}
